/**
 * ConnectFlow — Instagram Business Login (OAuth).
 *
 * Cliente clica "Conectar com Instagram" → backend retorna authorize_url →
 * abre popup Meta → cliente loga com IG → autoriza → callback envia
 * postMessage de volta → frontend marca como conectado.
 *
 * Não exige Tech Provider, não exige System User, não exige Page ID.
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Spinner } from 'react-bootstrap';
import {
  IconBrandInstagram,
  IconShieldCheck,
  IconMessage,
  IconCheck,
  IconAlertTriangle,
} from '@tabler/icons-react';
import useInstagramAccount from '../../../../../InstagramConfig/hooks/useInstagramAccount';
import InstagramApiService from '../../../../../InstagramConfig/services/InstagramApiService';
import './connectFlow.css';

const STEPS = {
  INTRO: 'intro',
  REDIRECTING: 'redirecting',
  WAITING: 'waiting',
  SELECT_PAGE: 'select_page',
  SUCCESS: 'success',
  ERROR: 'error',
};

const BENEFITS = [
  { icon: IconShieldCheck, text: 'Login OAuth oficial — sem precisar de tokens manuais' },
  { icon: IconMessage, text: 'Receba DMs no mesmo painel' },
  { icon: IconBrandInstagram, text: 'IA responde DMs (configurável por agente)' },
];

const POPUP_FEATURES = 'width=600,height=720,left=100,top=100,menubar=no,toolbar=no,location=no,status=no,resizable=yes,scrollbars=yes';

const ConnectFlow = ({ agentId, onConnected, onCancel }) => {
  const [step, setStep] = useState(STEPS.INTRO);
  const [errorMsg, setErrorMsg] = useState('');
  const [result, setResult] = useState(null);
  const [pageCandidates, setPageCandidates] = useState([]);
  const [selectionKey, setSelectionKey] = useState('');
  const [submittingPage, setSubmittingPage] = useState(false);
  const popupRef = useRef(null);
  const popupTimerRef = useRef(null);
  const successHandledRef = useRef(false);
  const { oauthStart, fetchStatus } = useInstagramAccount(agentId);

  // Marca como sucesso e atualiza estado. Usado tanto pelo postMessage
  // (fast path) quanto pelo polling de fallback (quando COOP bloqueia).
  const handleSuccess = useCallback((data) => {
    if (successHandledRef.current) return;
    successHandledRef.current = true;
    setResult({ username: data?.username || '' });
    setStep(STEPS.SUCCESS);
    fetchStatus();
  }, [fetchStatus]);

  const cleanupPopup = useCallback(() => {
    if (popupTimerRef.current) {
      clearInterval(popupTimerRef.current);
      popupTimerRef.current = null;
    }
    if (popupRef.current && !popupRef.current.closed) {
      popupRef.current.close();
    }
    popupRef.current = null;
  }, []);

  useEffect(() => () => cleanupPopup(), [cleanupPopup]);

  // Listener do postMessage do callback OAuth (fast path).
  useEffect(() => {
    const handler = (event) => {
      const data = event.data;
      if (!data || data.type !== 'instagram_oauth_result') return;

      cleanupPopup();
      if (data.ok) {
        handleSuccess(data);
      } else if (data.needs_page_selection && Array.isArray(data.candidates)) {
        setPageCandidates(data.candidates);
        setSelectionKey(data.selection_key || '');
        setStep(STEPS.SELECT_PAGE);
      } else {
        setErrorMsg(data.error || 'Erro ao conectar Instagram.');
        setStep(STEPS.ERROR);
      }
    };
    window.addEventListener('message', handler);
    return () => window.removeEventListener('message', handler);
  }, [cleanupPopup, handleSuccess]);

  // Polling de fallback: COOP do Chrome bloqueia postMessage entre
  // popup (Cloudflare tunnel) e parent (localhost). Polling consulta
  // getStatus() do agente — se conta foi criada, considera sucesso.
  // Só roda enquanto step === WAITING.
  useEffect(() => {
    if (step !== STEPS.WAITING) return undefined;

    let cancelled = false;
    const poll = async () => {
      if (cancelled || successHandledRef.current) return;
      try {
        const resp = await InstagramApiService.getStatus(agentId);
        if (!cancelled && resp?.data?.connected) {
          cleanupPopup();
          handleSuccess({ username: resp.data.username || '' });
        }
      } catch (err) {
        // Ignora — próximo tick tenta de novo
      }
    };

    const interval = setInterval(poll, 2000);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [step, agentId, cleanupPopup, handleSuccess]);

  const handleStart = async () => {
    setStep(STEPS.REDIRECTING);
    setErrorMsg('');
    try {
      const { authorize_url: authorizeUrl } = await oauthStart();
      if (!authorizeUrl) {
        throw new Error('Backend não retornou authorize_url');
      }

      const popup = window.open(authorizeUrl, 'ig_oauth', POPUP_FEATURES);
      if (!popup) {
        setStep(STEPS.ERROR);
        setErrorMsg(
          'Popup bloqueado pelo navegador. Permita popups deste site e tente novamente.',
        );
        return;
      }

      popupRef.current = popup;
      setStep(STEPS.WAITING);

      // Detecta se o usuário fechou o popup. Antes de declarar cancelamento,
      // faz um último check no backend — COOP do Chrome bloqueia postMessage
      // entre tunnel/localhost, então a conta pode estar criada mesmo sem
      // termos recebido o evento.
      popupTimerRef.current = setInterval(async () => {
        if (popupRef.current && popupRef.current.closed) {
          clearInterval(popupTimerRef.current);
          popupTimerRef.current = null;

          if (successHandledRef.current) return;

          // Last-chance check: aguarda 1.5s pra dar tempo do backend
          // persistir, depois consulta status.
          await new Promise((r) => setTimeout(r, 1500));
          try {
            const resp = await InstagramApiService.getStatus(agentId);
            if (resp?.data?.connected) {
              handleSuccess({ username: resp.data.username || '' });
              return;
            }
          } catch (err) {
            // ignora — vai cair no fluxo de cancelamento
          }

          setStep((prev) => {
            if (prev === STEPS.WAITING) {
              setErrorMsg('Login cancelado.');
              return STEPS.ERROR;
            }
            return prev;
          });
        }
      }, 500);
    } catch (err) {
      setErrorMsg(
        err.response?.data?.error || err.message || 'Erro ao iniciar OAuth.',
      );
      setStep(STEPS.ERROR);
    }
  };

  const handleFinish = () => {
    if (onConnected) onConnected(result);
  };

  const handleSelectPage = async (pageId) => {
    if (!selectionKey || submittingPage) return;
    setSubmittingPage(true);
    try {
      const resp = await InstagramApiService.selectPage(
        agentId, selectionKey, pageId,
      );
      const data = resp?.data || {};
      handleSuccess({ username: data.username || '' });
    } catch (err) {
      setErrorMsg(
        err.response?.data?.error
          || 'Falha ao concluir conexão. Tente reconectar.',
      );
      setStep(STEPS.ERROR);
    } finally {
      setSubmittingPage(false);
    }
  };

  return (
    <div className="igconnect">
      {step === STEPS.INTRO && (
        <div className="igconnect__intro">
          <div className="igconnect__intro-icon">
            <IconBrandInstagram size={32} />
          </div>
          <h3>Conectar Instagram</h3>
          <p style={{ color: '#6b7280', marginBottom: 16 }}>
            Faça login com sua conta Facebook para conectar a Página
            que tem sua conta Instagram Business vinculada.
          </p>

          <div className="igconnect__benefits">
            {BENEFITS.map((b, i) => (
              <div key={i} className="igconnect__benefit">
                <b.icon size={16} style={{ color: '#C13584' }} />
                <span>{b.text}</span>
              </div>
            ))}
          </div>

          <div
            style={{
              padding: 12,
              background: '#fff7ed',
              border: '1px solid #fed7aa',
              borderRadius: 8,
              fontSize: 12,
              color: '#9a3412',
              display: 'flex',
              gap: 8,
              alignItems: 'flex-start',
              marginTop: 16,
              textAlign: 'left',
            }}
          >
            <IconAlertTriangle size={14} style={{ flexShrink: 0, marginTop: 2 }} />
            <span>
              Pré-requisitos: ter uma <strong>Página Facebook</strong> que você
              administre, com sua conta <strong>Instagram Business</strong>{' '}
              vinculada (em business.facebook.com → Configurações da Página →
              Contas conectadas). Uma janela vai abrir para login no Facebook.
            </span>
          </div>

          <div
            style={{
              display: 'flex',
              gap: 8,
              justifyContent: 'flex-end',
              marginTop: 24,
            }}
          >
            <button type="button" onClick={onCancel} className="cloudapi-hero__action-btn">
              Cancelar
            </button>
            <button
              type="button"
              onClick={handleStart}
              className="igconnect__primary-btn"
            >
              <IconBrandInstagram size={16} style={{ marginRight: 6 }} />
              Conectar via Facebook
            </button>
          </div>
        </div>
      )}

      {step === STEPS.REDIRECTING && (
        <div style={{ padding: 48, textAlign: 'center' }}>
          <Spinner animation="border" style={{ color: '#C13584' }} />
          <p style={{ marginTop: 16, fontSize: 14, color: '#374151' }}>
            Abrindo Instagram...
          </p>
        </div>
      )}

      {step === STEPS.WAITING && (
        <div style={{ padding: 48, textAlign: 'center' }}>
          <Spinner animation="border" style={{ color: '#C13584' }} />
          <p style={{ marginTop: 16, fontSize: 14, color: '#374151' }}>
            Aguardando autorização no Instagram...
          </p>
          <p style={{ fontSize: 12, color: '#9ca3af', marginTop: 8 }}>
            Conclua o login na janela aberta. Você pode fechar essa mensagem
            quando terminar.
          </p>
          <button
            type="button"
            onClick={() => {
              cleanupPopup();
              setStep(STEPS.INTRO);
            }}
            className="cloudapi-hero__action-btn"
            style={{ marginTop: 16 }}
          >
            Cancelar
          </button>
        </div>
      )}

      {step === STEPS.SELECT_PAGE && (
        <div style={{ padding: 24 }}>
          <h4 style={{ marginBottom: 8 }}>Escolha qual conta conectar</h4>
          <p style={{ color: '#6b7280', fontSize: 13, marginBottom: 16 }}>
            Você administra várias Páginas Facebook com Instagram Business
            vinculado. Selecione qual deseja conectar a este agente:
          </p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {pageCandidates.map((c) => (
              <button
                key={c.page_id}
                type="button"
                disabled={submittingPage}
                onClick={() => handleSelectPage(c.page_id)}
                className="igconnect__primary-btn"
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'flex-start',
                  textAlign: 'left',
                  padding: '12px 16px',
                  background: '#fff',
                  color: '#111827',
                  border: '1px solid #e5e7eb',
                  cursor: submittingPage ? 'wait' : 'pointer',
                  opacity: submittingPage ? 0.6 : 1,
                }}
              >
                <strong>@{c.ig_username}</strong>
                <span style={{ fontSize: 12, color: '#6b7280' }}>
                  Página: {c.page_name}
                </span>
              </button>
            ))}
          </div>
          <button
            type="button"
            onClick={onCancel}
            className="cloudapi-hero__action-btn"
            style={{ marginTop: 16 }}
            disabled={submittingPage}
          >
            Cancelar
          </button>
        </div>
      )}

      {step === STEPS.SUCCESS && (
        <div style={{ padding: 32, textAlign: 'center' }}>
          <div
            style={{
              width: 64,
              height: 64,
              borderRadius: '50%',
              background: '#dcfce7',
              margin: '0 auto 16px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#16a34a',
            }}
          >
            <IconCheck size={32} />
          </div>
          <h4 style={{ marginBottom: 8 }}>Conectado com sucesso!</h4>
          {result?.username && (
            <p style={{ fontSize: 14, color: '#374151' }}>@{result.username}</p>
          )}
          <button
            type="button"
            onClick={handleFinish}
            className="igconnect__primary-btn"
            style={{ marginTop: 16 }}
          >
            Continuar
          </button>
        </div>
      )}

      {step === STEPS.ERROR && (
        <div style={{ padding: 32, textAlign: 'center' }}>
          <div
            style={{
              width: 64,
              height: 64,
              borderRadius: '50%',
              background: '#fef2f2',
              margin: '0 auto 16px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#dc2626',
            }}
          >
            <IconAlertTriangle size={32} />
          </div>
          <h4 style={{ marginBottom: 8 }}>Erro ao conectar</h4>
          <p style={{ color: '#6b7280', marginBottom: 16, fontSize: 13 }}>
            {errorMsg}
          </p>
          <div style={{ display: 'flex', gap: 8, justifyContent: 'center' }}>
            <button
              type="button"
              onClick={onCancel}
              className="cloudapi-hero__action-btn"
            >
              Cancelar
            </button>
            <button
              type="button"
              onClick={() => setStep(STEPS.INTRO)}
              className="igconnect__primary-btn"
            >
              Tentar novamente
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ConnectFlow;
